<script setup lang="ts">
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type Component,
} from 'vue';
import ArrowUpRight from 'lucide-vue-next/dist/esm/icons/arrow-up-right.js'; import AtSign from 'lucide-vue-next/dist/esm/icons/at-sign.js'; import Calculator from 'lucide-vue-next/dist/esm/icons/calculator.js'; import ChevronRight from 'lucide-vue-next/dist/esm/icons/chevron-right.js'; import CircleHelp from 'lucide-vue-next/dist/esm/icons/circle-question-mark.js'; import Clock from 'lucide-vue-next/dist/esm/icons/clock.js'; import CornerDownLeft from 'lucide-vue-next/dist/esm/icons/corner-down-left.js'; import Hash from 'lucide-vue-next/dist/esm/icons/hash.js'; import Link from 'lucide-vue-next/dist/esm/icons/link.js'; import Pin from 'lucide-vue-next/dist/esm/icons/pin.js'; import PinOff from 'lucide-vue-next/dist/esm/icons/pin-off.js'; import Search from 'lucide-vue-next/dist/esm/icons/search.js'; import Settings from 'lucide-vue-next/dist/esm/icons/settings.js';














import { i18nInjectionKey } from '../internal';
import { NxSuggestionRow } from '../SuggestionRow';
import { fold, isSuggestionEligible, words } from '../lib/textQuery';
import {
  NxCommandRegistryKey,
  type NxCommand,
  type NxCommandMemoryEntry,
  type NxCommandPreview,
  type NxCommandRegistry,
  type NxCommandScope,
  type NxCommandSource,
  type NxCommandSuggester,
  type NxCommandSuggestion,
} from './commands';
import {
  commandCategories,
  comboKey,
  comboMatches,
  completeKeyword,
  formatCombo,
  isEditableTarget,
  MODE_PREFIXES,
  parseCombo,
  parseScope,
  rankCommands,
  sourceCategory,
  type NxPaletteMode,
} from './engine';
import { highlight, markTokens, tokens, type MatchRange } from './match';
import { calculate, formatResult } from './calc';

const registry = inject<NxCommandRegistry | null>(NxCommandRegistryKey, null);
const i18n = inject(i18nInjectionKey, null);

const props = withDefaults(
  defineProps<{
    /** Placeholder of the search input */
    placeholder?: string;
    /** Empty-state label when nothing matches */
    emptyLabel?: string;
    /** Optional hint on the right of the input. Empty by default: the UI shows no ⌘K anywhere. */
    shortcutHint?: string;
    /** Shown while a search source is still loading */
    searchingLabel?: string;
    /** Pause after the last keystroke before the search sources are asked (ms) */
    searchDelay?: number;
    /** Lift exact search hits into a „Beste Treffer“ group on top. */
    topHits?: boolean;
    /** Commands per group while searching (the `>` and `/` modes show all). */
    groupLimit?: number;
  }>(),
  {
    placeholder: undefined,
    emptyLabel: undefined,
    shortcutHint: '',
    searchingLabel: undefined,
    searchDelay: 200,
    topHits: true,
    groupLimit: 6,
  }
);

// ── Texts ──────────────────────────────────────────────────────────────────

const FALLBACK: Record<string, string> = {
  placeholder: 'Suche',
  empty: 'Keine Treffer',
  searching: 'Suche…',
  top_hits: 'Beste Treffer',
  pinned: 'Angeheftet',
  recent: 'Zuletzt benutzt',
  unavailable: 'Gerade nicht erreichbar',
  loading: 'Lädt…',
  results: '{count} Treffer',
  input_confirm: 'Mit Enter bestätigen',
  open: 'Öffnen',
  open_new_tab: 'In neuem Tab öffnen',
  copy_link: 'Link kopieren',
  pin: 'Anheften',
  unpin: 'Lösen',
  calc_copy: 'Ergebnis kopieren',
  calc_invalid: 'Rechnung eingeben, z. B. 19 * 1,19',
  help_prefixes: 'Präfixe',
  help_shortcuts: 'Tastenkürzel',
  mode_commands: 'Befehle',
  mode_records: 'Datensätze',
  mode_people: 'Personen',
  mode_settings: 'Einstellungen',
  mode_help: 'Hilfe',
  mode_calc: 'Rechner',
  key_mod: 'Strg',
  key_shift: 'Umschalt',
  key_alt: 'Alt',
  footer_move: 'auswählen',
  footer_open: 'öffnen',
  footer_new_tab: 'neuer Tab',
  footer_actions: 'Aktionen',
  footer_close: 'schließen',
  key_up: '↑',
  key_down: '↓',
  key_enter: '↵',
  key_tab: 'Tab',
  key_esc: 'Esc',
  suggestion_prefix: 'Meinten Sie',
  suggestion_keep: 'Ignorieren',
  suggestion_confirm: '„{original}“ ist richtig',
  suggestion_never_suggest: '„{text}“ nicht mehr vorschlagen',
};

function t(key: string, params: Record<string, unknown> = {}): string {
  let out = FALLBACK[key] ?? key;
  if (i18n) {
    const full = `common.command_palette.${key}`;
    const v = i18n.t(full, params);
    if (v && v !== full) out = v;
  }
  return out.replace(/\{(\w+)\}/g, (_, k: string) => String(params[k] ?? ''));
}

const MODE_ICONS: Record<NxPaletteMode, Component> = {
  commands: ChevronRight,
  records: Hash,
  people: AtSign,
  settings: Settings,
  help: CircleHelp,
  calc: Calculator,
};

// ── State ──────────────────────────────────────────────────────────────────

interface Frame {
  id: number;
  title: string;
  /** null while the children load */
  items: NxCommand[] | null;
  input?: NxCommand['input'];
}

const query = ref('');
const mode = ref<NxPaletteMode | null>(null);
const scope = ref<{ source: NxCommandSource; scope: NxCommandScope } | null>(null);
const stack = ref<Frame[]>([]);
const activeIdx = ref(0);
const inputRef = ref<HTMLInputElement | null>(null);
const listRef = ref<HTMLDivElement | null>(null);
const uid = `nx-cmd-${Math.random().toString(36).slice(2, 9)}`;
let frameSeq = 0;

const atRoot = computed(() => stack.value.length === 0);
const frame = computed(() => stack.value[stack.value.length - 1]);
const queryTokens = computed(() => tokens(query.value));

function isOpen() {
  return registry?.isOpen.value ?? false;
}

/** Takes typed text; a mode prefix or a scope keyword turns into a chip. */
function applyText(raw: string) {
  let text = raw;
  if (atRoot.value) {
    if (!mode.value && !scope.value) {
      const m = MODE_PREFIXES[text.charAt(0)];
      if (m) {
        mode.value = m;
        text = text.slice(1).trimStart();
      }
    }
    if (
      !scope.value &&
      (mode.value === null || mode.value === 'records' || mode.value === 'people')
    ) {
      const s = parseScope(text, sources.value);
      if (s) {
        scope.value = { source: s.source, scope: s.scope };
        text = s.rest;
      }
    }
  }
  query.value = text;
  if (inputRef.value && inputRef.value.value !== text) inputRef.value.value = text;
}

function onInput(e: Event) {
  applyText((e.target as HTMLInputElement).value);
}

// ── Sources ────────────────────────────────────────────────────────────────

const sources = computed<NxCommandSource[]>(() =>
  [...(registry?.sources?.value ?? [])]
    .map((s, i) => ({ s, i }))
    .sort((a, b) => (a.s.order ?? 1000) - (b.s.order ?? 1000) || a.i - b.i)
    .map(({ s }) => s)
);

const suggesters = computed(() => registry?.suggesters?.value ?? []);

// Sources can arrive after the user typed a keyword (catalogs load on first
// use): turn a keyword that became known into its chip then.
watch(sources, () => {
  if (atRoot.value && !scope.value && query.value) applyText(query.value);
});

type SourceState = { status: 'loading' | 'done' | 'failed'; items: NxCommand[] };
const sourceResults = ref(new Map<string, SourceState>());
let searchTimer: ReturnType<typeof setTimeout> | undefined;
let searchAbort: AbortController | null = null;

function cancelSearch() {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = undefined;
  searchAbort?.abort();
  searchAbort = null;
}

const eligibleSources = computed<NxCommandSource[]>(() => {
  if (!atRoot.value) return [];
  const q = query.value.trim();
  if (scope.value) return q.length >= 1 ? [scope.value.source] : [];
  const cat = sourceCategory(mode.value);
  if (!cat) return [];
  return sources.value.filter(
    (s) =>
      q.length >= (s.minQueryLength ?? 2) && (cat === 'all' || (s.category ?? 'record') === cat)
  );
});

const searchKey = computed(
  () =>
    `${query.value.trim()}\u0000${scope.value?.scope.id ?? ''}\u0000${eligibleSources.value
      .map((s) => s.id)
      .join(',')}`
);

function runSources(q: string, list: NxCommandSource[], scopeId?: string) {
  const controller = new AbortController();
  searchAbort = controller;
  for (const source of list) {
    const pending = scopeId
      ? source.search(q, controller.signal, { scope: scopeId })
      : source.search(q, controller.signal);
    pending
      .then((items) => {
        if (controller.signal.aborted) return;
        setSourceState(source.id, { status: 'done', items });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.warn('[NxCommandPalette] search source failed', source.id, err);
        setSourceState(source.id, { status: 'failed', items: [] });
      });
  }
}

function setSourceState(id: string, state: SourceState) {
  const next = new Map(sourceResults.value);
  next.set(id, state);
  sourceResults.value = next;
}

watch(searchKey, () => {
  cancelSearch();
  const list = eligibleSources.value;
  // Results of the previous query must not linger under the new one.
  sourceResults.value = new Map(
    list.map((s) => [s.id, { status: 'loading', items: [] } as SourceState])
  );
  if (list.length === 0) return;
  const q = query.value.trim();
  const scopeId = scope.value?.scope.id;
  searchTimer = setTimeout(() => runSources(q, list, scopeId), props.searchDelay);
});

// ── Spelling suggestion ────────────────────────────────────────────────────
// One row under the input. It is bound to the query it was asked for: a newer
// query aborts the older call and drops a late answer, and any change of the
// query hides the row at once. The row never filters or replaces the hits.

interface ShownSuggestion {
  text: string;
  /** The last word the suggestion corrects. */
  original: string;
  /** The suggester whose suggestion won. */
  suggesterId: string;
  /** `false`: the row has no confirm link. */
  confirmable: boolean;
}

const suggestion = ref<ShownSuggestion | null>(null);
/** Free-text queries dismissed with keep or confirm during this open. */
let suggestionKept = new Set<string>();
let suggestTimer: ReturnType<typeof setTimeout> | undefined;
let suggestAbort: AbortController | null = null;
let suggestGeneration = 0;

function cancelSuggest() {
  if (suggestTimer) clearTimeout(suggestTimer);
  suggestTimer = undefined;
  suggestAbort?.abort();
  suggestAbort = null;
}

/** The last word of the free text, with its range in that text. */
function lastWord(text: string): { text: string; start: number; end: number } | null {
  const found = words(text);
  if (found.length === 0) return null;
  const last = found[found.length - 1]!;
  return { text: text.slice(last.start, last.end), start: last.start, end: last.end };
}

function scheduleSuggest() {
  cancelSuggest();
  const generation = ++suggestGeneration;
  suggestion.value = null;
  if (!atRoot.value) return;
  const list = suggesters.value;
  if (list.length === 0 || suggestionKept.has(query.value)) return;
  const word = lastWord(query.value);
  if (!word || !isSuggestionEligible(word.text)) return;
  const term = word.text;
  const scopeId = scope.value?.scope.id;
  const generationQuery = query.value;
  suggestTimer = setTimeout(() => {
    if (generation !== suggestGeneration) return;
    const controller = new AbortController();
    suggestAbort = controller;
    const jobs = list.map((suggester) => {
      const pending = suggester.suggest(
        term,
        controller.signal,
        scopeId ? { scope: scopeId, query: generationQuery } : { query: generationQuery }
      );
      return pending.then(
        (items) => (Array.isArray(items) ? items : []),
        () => [] as NxCommandSuggestion[]
      );
    });
    void Promise.all(jobs).then((groups) => {
      if (generation !== suggestGeneration || controller.signal.aborted) return;
      if (query.value !== generationQuery || suggestionKept.has(query.value)) return;
      const foldedWord = fold(term).text;
      let best: NxCommandSuggestion | null = null;
      let bestSuggester: NxCommandSuggester | null = null;
      for (const [index, group] of groups.entries()) {
        for (const item of group) {
          if (!item || typeof item.text !== 'string' || !item.text) continue;
          if (!Number.isFinite(item.score)) continue;
          if (fold(item.text).text === foldedWord) continue;
          if (!best || item.score > best.score) {
            best = item;
            bestSuggester = list[index]!;
          }
        }
      }
      if (!best || !bestSuggester) return;
      suggestion.value = {
        text: best.text,
        original: term,
        suggesterId: bestSuggester.id,
        confirmable: best.confirmable !== false,
      };
    });
  }, props.searchDelay);
}

watch(
  () => [query.value, scope.value?.scope.id ?? '', atRoot.value, suggesters.value] as const,
  () => scheduleSuggest()
);

/** The confirm link's template. `{original}` stays for the row, which substitutes the typed word literally. */
const suggestionConfirmLabel = computed(() => t('suggestion_confirm', { original: '{original}' }));

/** The never-suggest link's template. `{text}` stays for the row, which substitutes the suggested word literally. */
const suggestionNeverSuggestLabel = computed(() =>
  t('suggestion_never_suggest', { text: '{text}' })
);

/** Only a suggestion whose suggester implements `neverSuggest` offers the link. */
const canNeverSuggest = computed(() => {
  const shown = suggestion.value;
  if (!shown) return false;
  return (
    typeof suggesters.value.find((s) => s.id === shown.suggesterId)?.neverSuggest === 'function'
  );
});

/** The action removes the focused link. Put the caret back in the query. */
function focusQueryInput() {
  void nextTick(() => inputRef.value?.focus());
}

function acceptSuggestion() {
  const shown = suggestion.value;
  if (!shown) return;
  const word = lastWord(query.value);
  if (!word || word.text !== shown.original) return;
  const next = query.value.slice(0, word.start) + shown.text + query.value.slice(word.end);
  suggestion.value = null;
  applyText(next);
  focusQueryInput();
  for (const suggester of suggesters.value) suggester.accept?.(shown.original, shown.text);
}

function keepSuggestion() {
  suggestionKept.add(query.value);
  suggestion.value = null;
  focusQueryInput();
}

function confirmSuggestion() {
  const shown = suggestion.value;
  suggestionKept.add(query.value);
  suggestion.value = null;
  focusQueryInput();
  if (!shown) return;
  for (const suggester of suggesters.value) suggester.confirm?.(shown.original);
}

/** A never-suggest hook that returned a promise is still running: the link is answered once. */
let neverSuggestPending = false;

/**
 * „„X“ nicht mehr vorschlagen“. A hook that finishes at once hides the row. A
 * hook that returns a promise is awaited: its resolution hides the row, its
 * rejection (or a synchronous throw) keeps the row, so the user can try again.
 * Showing the failure is the suggester's own business.
 */
async function neverSuggestSuggestion() {
  if (neverSuggestPending) return;
  const shown = suggestion.value;
  if (!shown) return;
  const suggester = suggesters.value.find((s) => s.id === shown.suggesterId);
  if (!suggester?.neverSuggest) return;
  const typed = query.value;
  const hide = () => {
    suggestionKept.add(typed);
    if (suggestion.value === shown) suggestion.value = null;
    focusQueryInput();
  };
  let outcome: void | Promise<void>;
  try {
    outcome = suggester.neverSuggest(shown.original, shown.text);
  } catch {
    focusQueryInput();
    return;
  }
  if (typeof (outcome as Promise<void> | undefined)?.then !== 'function') {
    hide();
    return;
  }
  neverSuggestPending = true;
  try {
    await outcome;
  } catch {
    focusQueryInput();
    return;
  } finally {
    neverSuggestPending = false;
  }
  hide();
}

function targetInSuggestion(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest('[data-testid="nx-suggestion-row"]');
}

const searching = computed(() =>
  [...sourceResults.value.values()].some((state) => state.status === 'loading')
);

// ── Entries ────────────────────────────────────────────────────────────────

interface Entry {
  key: string;
  cmd: NxCommand;
  ranges: MatchRange[];
}

interface Section {
  key: string;
  title: string;
  subtitle?: string;
  entries: Entry[];
  status?: 'loading' | 'failed';
  icon?: Component;
}

const commands = computed(() => registry?.commands.value ?? []);

function fromMemory(entry: NxCommandMemoryEntry): NxCommand {
  const live = commands.value.find((c) => c.id === entry.id);
  if (live) return live;
  return {
    id: entry.id,
    label: entry.label,
    hint: entry.hint,
    group: entry.group,
    href: entry.href,
    category: entry.category,
    run: () => {
      if (entry.href) registry?.navigate.value?.(entry.href);
    },
  };
}

function plain(cmd: NxCommand, key = cmd.id): Entry {
  return { key, cmd, ranges: [] };
}

function groupSections(entries: Entry[], limit: number | null, prefix: string): Section[] {
  const byGroup = new Map<string, Entry[]>();
  for (const e of entries) {
    const g = e.cmd.group ?? '';
    if (!byGroup.has(g)) byGroup.set(g, []);
    byGroup.get(g)!.push(e);
  }
  return [...byGroup].map(([name, list]) => ({
    key: `${prefix}:${name}`,
    title: name,
    entries: limit ? list.slice(0, limit) : list,
  }));
}

function rootSections(): Section[] {
  const q = query.value.trim();
  const out: Section[] = [];
  const memory = registry?.memory.value ?? { recents: [], pins: [] };
  const context = registry?.context.value;

  // Empty query: pins, recents, then everything (the open app's first).
  if (!q && !scope.value) {
    if (!mode.value) {
      if (memory.pins.length) {
        out.push({
          key: 'pinned',
          title: t('pinned'),
          icon: Pin,
          entries: memory.pins.map((p) => plain(fromMemory(p), `pin:${p.id}`)),
        });
      }
      if (memory.recents.length) {
        out.push({
          key: 'recent',
          title: t('recent'),
          icon: Clock,
          entries: memory.recents.map((r) => plain(fromMemory(r), `recent:${r.id}`)),
        });
      }
    }
    const ranked = rankCommands(commands.value, '', {
      categories: commandCategories(mode.value),
      context,
    });
    const app = context?.app;
    const ordered = app
      ? [...ranked.filter((r) => r.cmd.app === app), ...ranked.filter((r) => r.cmd.app !== app)]
      : ranked;
    out.push(
      ...groupSections(
        ordered.map((r) => ({ key: r.cmd.id, cmd: r.cmd, ranges: r.ranges })),
        null,
        'cmd'
      )
    );
    return out;
  }

  // Commands — not while a scope narrows the query to one source.
  let commandEntries: Entry[] = [];
  if (!scope.value) {
    commandEntries = rankCommands(commands.value, q, {
      categories: commandCategories(mode.value),
      context,
      recentIds: memory.recents.map((e) => e.id),
    }).map((r) => ({ key: r.cmd.id, cmd: r.cmd, ranges: r.ranges }));
  }

  // Search sources in the account's order; exact hits rise to the top.
  const top: Entry[] = [];
  const sourceSections: Section[] = [];
  for (const source of eligibleSources.value) {
    const state = sourceResults.value.get(source.id);
    const title = source.group ?? source.id;
    if (!state || state.status !== 'done') {
      sourceSections.push({
        key: `src:${source.id}`,
        title,
        entries: [],
        status: state?.status === 'failed' ? 'failed' : 'loading',
      });
      continue;
    }
    const bySub = new Map<string, Entry[]>();
    for (const item of state.items) {
      const entry: Entry = {
        key: `${source.id}:${item.id}`,
        cmd: item,
        ranges: markTokens(item.label, queryTokens.value),
      };
      if (props.topHits && item.match === 'exact') {
        top.push(entry);
        continue;
      }
      const sub = item.subgroup ?? '';
      if (!bySub.has(sub)) bySub.set(sub, []);
      bySub.get(sub)!.push(entry);
    }
    for (const [sub, entries] of bySub) {
      sourceSections.push({
        key: `src:${source.id}:${sub}`,
        title,
        subtitle: sub || undefined,
        entries,
      });
    }
  }

  if (top.length) out.push({ key: 'top', title: t('top_hits'), entries: top });
  const unlimited = mode.value === 'commands' || mode.value === 'settings';
  out.push(...groupSections(commandEntries, unlimited ? null : props.groupLimit, 'cmd'));
  out.push(...sourceSections);

  const anything = out.some((s) => s.entries.length > 0);
  const fallbacks = registry?.fallbacks.value ?? [];
  if (!anything && !searching.value && fallbacks.length) {
    const raw = scope.value ? `${scope.value.scope.label} ${q}` : q;
    out.push({
      key: 'fallback',
      title: '',
      entries: fallbacks.map((f) =>
        plain(
          {
            id: `fallback:${f.id}`,
            label: f.label(raw),
            icon: f.icon,
            transient: true,
            run: () => f.run(raw),
          },
          `fallback:${f.id}`
        )
      ),
    });
  }
  return out;
}

function frameSections(): Section[] {
  const f = frame.value;
  if (!f) return [];
  if (f.input) {
    const value = query.value;
    const input = f.input;
    return [
      {
        key: 'input',
        title: f.title,
        entries: [
          plain(
            {
              id: 'input:confirm',
              label: value || input.placeholder || f.title,
              hint: t('input_confirm'),
              icon: CornerDownLeft,
              transient: true,
              run: () => input.run(value),
            },
            'input:confirm'
          ),
        ],
      },
    ];
  }
  if (f.items === null) return [{ key: 'frame', title: f.title, entries: [], status: 'loading' }];
  return [
    {
      key: 'frame',
      title: f.title,
      entries: rankCommands(f.items, query.value).map((r) => ({
        key: r.cmd.id,
        cmd: r.cmd,
        ranges: r.ranges,
      })),
    },
  ];
}

function helpSections(): Section[] {
  const prefixes: Entry[] = Object.entries(MODE_PREFIXES).map(([prefix, m]) =>
    plain(
      {
        id: `help:mode:${m}`,
        label: t(`mode_${m}`),
        shortcut: prefix,
        icon: MODE_ICONS[m],
        transient: true,
        run: () => {},
      },
      `help:mode:${m}`
    )
  );
  const shortcuts: Entry[] = commands.value
    .filter((c) => c.bind || c.shortcut)
    .map((c) => ({
      key: `help:key:${c.id}`,
      cmd: { ...c, shortcut: c.bind ? formatBind(c.bind) : c.shortcut },
      ranges: [],
    }));
  const keep = (e: Entry) => !query.value.trim() || rankCommands([e.cmd], query.value).length > 0;
  return [
    { key: 'help:prefixes', title: t('help_prefixes'), entries: prefixes.filter(keep) },
    { key: 'help:shortcuts', title: t('help_shortcuts'), entries: shortcuts.filter(keep) },
  ].filter((s) => s.entries.length);
}

function calcSections(): Section[] {
  const value = calculate(query.value);
  if (value === null) {
    return [
      {
        key: 'calc',
        title: t('mode_calc'),
        entries: [
          plain(
            {
              id: 'calc:hint',
              label: t('calc_invalid'),
              icon: Calculator,
              transient: true,
              run: () => {},
            },
            'calc:hint'
          ),
        ],
      },
    ];
  }
  const text = formatResult(value, i18n?.locale?.value ?? 'de');
  return [
    {
      key: 'calc',
      title: t('mode_calc'),
      entries: [
        plain(
          {
            id: 'calc:result',
            label: `= ${text}`,
            hint: t('calc_copy'),
            icon: Calculator,
            transient: true,
            run: () => copyText(text),
          },
          'calc:result'
        ),
      ],
    },
  ];
}

const sections = computed<Section[]>(() => {
  if (!atRoot.value) return frameSections();
  if (mode.value === 'help') return helpSections();
  if (mode.value === 'calc') return calcSections();
  return rootSections();
});

const flat = computed(() => sections.value.flatMap((s) => s.entries));
const liveStatus = computed(() => {
  const results = t('results', { count: flat.value.length });
  if (!suggestion.value) return results;
  return `${results} ${t('suggestion_prefix')} ${suggestion.value.text}`;
});
const activeEntry = computed<Entry | undefined>(() => flat.value[activeIdx.value]);
const hasContent = computed(() => sections.value.some((s) => s.entries.length || s.status));

watch(
  () => [query.value, mode.value, scope.value?.scope.id, stack.value.length],
  () => {
    activeIdx.value = 0;
  }
);

// Arriving results can shift entries; keep the highlighted entry highlighted.
watch(flat, (next, prev) => {
  const active = prev?.[activeIdx.value];
  const idx = active ? next.findIndex((e) => e.key === active.key) : -1;
  if (idx >= 0) activeIdx.value = idx;
  else if (activeIdx.value >= next.length) activeIdx.value = 0;
});

// ── Preview ────────────────────────────────────────────────────────────────

const preview = ref<NxCommandPreview | null>(null);
let previewToken = 0;
watch(
  [activeEntry, () => registry?.isOpen.value],
  async ([entry, open]) => {
    const token = ++previewToken;
    const p = open ? entry?.cmd.preview : undefined;
    if (!p) {
      preview.value = null;
      return;
    }
    if (typeof p !== 'function') {
      preview.value = p;
      return;
    }
    try {
      const loaded = await p();
      if (token === previewToken) preview.value = loaded;
    } catch {
      if (token === previewToken) preview.value = null;
    }
  },
  { immediate: true }
);

// ── Running ────────────────────────────────────────────────────────────────

function reset() {
  query.value = '';
  mode.value = null;
  scope.value = null;
  stack.value = [];
  activeIdx.value = 0;
  suggestion.value = null;
  suggestionKept = new Set();
  cancelSearch();
  cancelSuggest();
  sourceResults.value = new Map();
}

function close() {
  registry?.close();
  reset();
}

function setQuery(text: string) {
  query.value = text;
  if (inputRef.value) inputRef.value.value = text;
  nextTick(() => inputRef.value?.focus());
}

async function pick(entry: Entry | undefined) {
  if (!entry) return;
  const cmd = entry.cmd;
  if (cmd.children) {
    const load = cmd.children;
    const id = ++frameSeq;
    stack.value = [...stack.value, { id, title: cmd.label, items: null }];
    setQuery('');
    try {
      const items = await load();
      stack.value = stack.value.map((f) => (f.id === id ? { ...f, items } : f));
    } catch (err) {
      console.error('[NxCommandPalette] children failed', cmd.id, err);
      stack.value = stack.value.filter((f) => f.id !== id);
    }
    return;
  }
  if (cmd.input) {
    stack.value = [
      ...stack.value,
      { id: ++frameSeq, title: cmd.label, items: [], input: cmd.input },
    ];
    setQuery('');
    return;
  }
  close();
  registry?.remember(cmd);
  try {
    await cmd.run();
  } catch (err) {
    console.error('[NxCommandPalette] command failed', cmd.id, err);
  }
}

function resolveUrl(href: string): string {
  const resolved = registry?.resolveUrl.value?.(href) ?? href;
  try {
    return new URL(resolved, window.location.href).toString();
  } catch {
    return resolved;
  }
}

function openInNewTab(cmd: NxCommand | undefined) {
  if (!cmd?.href) return;
  window.open(resolveUrl(cmd.href), '_blank', 'noopener');
  registry?.remember(cmd);
  close();
}

function copyText(text: string) {
  void navigator.clipboard?.writeText(text).catch(() => {});
}

function copyLink(cmd: NxCommand | undefined) {
  if (!cmd?.href) return;
  copyText(resolveUrl(cmd.href));
  close();
}

function actionsOf(cmd: NxCommand): NxCommand[] {
  const list: NxCommand[] = [
    {
      id: 'action:open',
      label: t('open'),
      icon: CornerDownLeft,
      transient: true,
      run: async () => {
        registry?.remember(cmd);
        await cmd.run();
      },
    },
    ...(cmd.actions ?? []),
  ];
  if (cmd.href) {
    list.push(
      {
        id: 'action:new-tab',
        label: t('open_new_tab'),
        icon: ArrowUpRight,
        shortcut: formatBind('mod+enter'),
        transient: true,
        run: () => openInNewTab(cmd),
      },
      {
        id: 'action:copy-link',
        label: t('copy_link'),
        icon: Link,
        shortcut: formatBind('shift+enter'),
        transient: true,
        run: () => copyLink(cmd),
      }
    );
  }
  if (registry) {
    list.push(
      registry.isPinned(cmd.id)
        ? {
            id: 'action:unpin',
            label: t('unpin'),
            icon: PinOff,
            transient: true,
            run: () => registry.unpin(cmd.id),
          }
        : {
            id: 'action:pin',
            label: t('pin'),
            icon: Pin,
            transient: true,
            run: () => registry.pin(cmd),
          }
    );
  }
  return list;
}

function openActions(entry: Entry | undefined): boolean {
  if (!entry || entry.cmd.transient) return false;
  stack.value = [
    ...stack.value,
    { id: ++frameSeq, title: entry.cmd.label, items: actionsOf(entry.cmd) },
  ];
  setQuery('');
  return true;
}

/** One step back: sub-list, then scope chip, then mode chip. */
function back(): boolean {
  if (stack.value.length) {
    stack.value = stack.value.slice(0, -1);
    setQuery('');
    return true;
  }
  if (scope.value) {
    const keyword = scope.value.scope.keywords[0] ?? '';
    scope.value = null;
    setQuery(keyword);
    return true;
  }
  if (mode.value) {
    mode.value = null;
    return true;
  }
  return false;
}

function move(delta: number) {
  const max = flat.value.length;
  if (max === 0) return;
  activeIdx.value = (activeIdx.value + delta + max) % max;
  nextTick(() => scrollActiveIntoView());
}

function scrollActiveIntoView() {
  const el = listRef.value?.querySelector<HTMLElement>('.cn-cmd-item.active');
  if (el) el.scrollIntoView({ block: 'nearest' });
}

function onPaletteKeydown(e: KeyboardEvent) {
  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      move(1);
      return;
    case 'ArrowUp':
      e.preventDefault();
      move(-1);
      return;
    case 'Enter':
      // The suggestion row's links take Enter themselves. In the input, Enter
      // still runs the active entry.
      if (targetInSuggestion(e.target)) return;
      e.preventDefault();
      if (e.metaKey || e.ctrlKey) openInNewTab(activeEntry.value?.cmd);
      else if (e.shiftKey) copyLink(activeEntry.value?.cmd);
      else void pick(activeEntry.value);
      return;
    case 'Tab': {
      // While the row is up, Tab moves from the input onto its links.
      if (suggestion.value && (targetInSuggestion(e.target) || e.target === inputRef.value)) {
        return;
      }
      e.preventDefault();
      const keyword =
        atRoot.value && !scope.value ? completeKeyword(query.value, sources.value) : null;
      if (keyword) applyText(`${keyword} `);
      else openActions(activeEntry.value);
      return;
    }
    case 'ArrowRight': {
      const input = inputRef.value;
      if (input && input.selectionStart === input.value.length && openActions(activeEntry.value)) {
        e.preventDefault();
      }
      return;
    }
    case 'Backspace':
      if (!query.value && back()) e.preventDefault();
      return;
    case 'Escape':
      e.preventDefault();
      if (!stack.value.length || !back()) close();
      return;
  }
}

// ── Global keys ────────────────────────────────────────────────────────────

const mac = /Mac|iPhone|iPad/.test(globalThis.navigator?.platform ?? '');

function formatBind(spec: string): string {
  return formatCombo(spec, mac, { mod: t('key_mod'), shift: t('key_shift'), alt: t('key_alt') });
}

const bindings = computed(() =>
  commands.value
    .filter((c) => c.bind)
    .map((c) => ({ cmd: c, combo: parseCombo(c.bind!) }))
    .filter((b): b is { cmd: NxCommand; combo: NonNullable<typeof b.combo> } => !!b.combo)
);

let warnedConflicts = '';
watch(
  bindings,
  (list) => {
    const seen = new Map<string, string>();
    const conflicts: string[] = [];
    for (const b of list) {
      const k = comboKey(b.cmd.bind!);
      const other = seen.get(k);
      if (other && other !== b.cmd.id) conflicts.push(`${k}: ${other} / ${b.cmd.id}`);
      seen.set(k, b.cmd.id);
    }
    const text = conflicts.join('; ');
    if (text && text !== warnedConflicts)
      console.warn('[NxCommandPalette] conflicting shortcuts', text);
    warnedConflicts = text;
  },
  { immediate: true }
);

/** A function key a command lets fire in a text field (`bindWhenTyping`). */
function firesWhenTyping(b: { cmd: NxCommand; combo: { key: string } }): boolean {
  return !!b.cmd.bindWhenTyping && /^f([1-9]|1[0-2])$/.test(b.combo.key);
}

function onGlobalKeydown(e: KeyboardEvent) {
  if (isOpen()) {
    // Keys typed while the palette is open belong to it, wherever focus went.
    if (!e.defaultPrevented) onPaletteKeydown(e);
    return;
  }
  if (e.defaultPrevented || e.repeat) return;
  for (const b of bindings.value) {
    if (!comboMatches(b.combo, e)) continue;
    if (!b.combo.mod && !firesWhenTyping(b) && isEditableTarget(e.target)) continue;
    if (b.cmd.when && !b.cmd.when()) continue;
    e.preventDefault();
    registry?.remember(b.cmd);
    void Promise.resolve()
      .then(() => b.cmd.run())
      .catch((err: unknown) => console.error('[NxCommandPalette] shortcut failed', b.cmd.id, err));
    return;
  }
}

watch(
  () => [registry?.isOpen.value, registry?.openCount.value] as const,
  ([open]) => {
    reset();
    if (!open) return;
    const initial = registry?.initialQuery.value ?? '';
    nextTick(() => {
      if (initial) applyText(initial);
      inputRef.value?.focus();
    });
  }
);

onMounted(() => window.addEventListener('keydown', onGlobalKeydown));
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onGlobalKeydown);
  cancelSearch();
  cancelSuggest();
});

// ── Rendering helpers ──────────────────────────────────────────────────────

const indexByKey = computed(() => new Map(flat.value.map((e, i) => [e.key, i])));

function indexOf(entry: Entry) {
  return indexByKey.value.get(entry.key) ?? -1;
}

function optionId(entry: Entry) {
  return `${uid}-opt-${indexOf(entry)}`;
}

function labelParts(entry: Entry) {
  return highlight(entry.cmd.label, entry.ranges);
}

function snippetParts(cmd: NxCommand) {
  const text = cmd.snippet ?? '';
  return highlight(text, markTokens(text, queryTokens.value));
}

function shortcutOf(cmd: NxCommand) {
  return cmd.shortcut ?? (cmd.bind ? formatBind(cmd.bind) : '');
}

/** A source's group heading shows once above its first subgroup. */
function showTitle(index: number) {
  const s = sections.value[index];
  const prev = sections.value[index - 1];
  return (
    !!s.title && !(prev && prev.title === s.title && s.subtitle && prev.key.startsWith('src:'))
  );
}

const chip = computed<{ label: string; icon: Component } | null>(() => {
  if (!atRoot.value) return null;
  if (scope.value) return { label: scope.value.scope.label, icon: scope.value.scope.icon ?? Hash };
  if (mode.value) return { label: t(`mode_${mode.value}`), icon: MODE_ICONS[mode.value] };
  return null;
});

const placeholderText = computed(
  () => frame.value?.input?.placeholder ?? props.placeholder ?? t('placeholder')
);

defineExpose({ back });
</script>

<template>
  <Teleport to="body">
    <div v-if="registry?.isOpen.value" class="cn-cmd-overlay" @mousedown.self="close">
      <div
        class="cn-cmd-panel"
        :class="{ 'has-preview': !!preview }"
        role="dialog"
        aria-modal="true"
        :aria-label="placeholderText"
      >
        <div class="cn-cmd-search">
          <Search class="cn-cmd-search-icon nxt-icon-md" />
          <span v-for="(f, fi) in stack" :key="`crumb:${fi}`" class="cn-cmd-chip cn-cmd-crumb">
            {{ f.title }}
          </span>
          <span v-if="chip" class="cn-cmd-chip">
            <component :is="chip.icon" class="nxt-icon-sm" />
            {{ chip.label }}
          </span>
          <input
            ref="inputRef"
            :value="query"
            type="text"
            class="cn-cmd-input"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded="true"
            :aria-controls="`${uid}-list`"
            :aria-activedescendant="activeEntry ? optionId(activeEntry) : undefined"
            :placeholder="placeholderText"
            autocomplete="off"
            spellcheck="false"
            @input="onInput"
          />
          <span v-if="shortcutHint" class="cn-cmd-hint">{{ shortcutHint }}</span>
        </div>

        <div v-if="suggestion" class="cn-cmd-suggestion">
          <NxSuggestionRow
            :text="suggestion.text"
            :original="suggestion.original"
            :prefix-label="t('suggestion_prefix')"
            :keep-label="t('suggestion_keep')"
            :confirm-label="suggestionConfirmLabel"
            :confirmable="suggestion.confirmable"
            :never-suggest="canNeverSuggest"
            :never-suggest-label="suggestionNeverSuggestLabel"
            @accept="acceptSuggestion"
            @keep="keepSuggestion"
            @confirm="confirmSuggestion"
            @never-suggest="neverSuggestSuggestion"
          />
        </div>

        <div class="cn-cmd-body">
          <div :id="`${uid}-list`" ref="listRef" class="cn-cmd-list" role="listbox">
            <template v-if="hasContent">
              <div
                v-for="(s, si) in sections"
                :key="s.key"
                class="cn-cmd-group"
                role="group"
                :aria-label="[s.title, s.subtitle].filter(Boolean).join(' › ') || undefined"
              >
                <div v-if="showTitle(si)" class="cn-cmd-group-label">{{ s.title }}</div>
                <div v-if="s.subtitle" class="cn-cmd-subgroup-label">{{ s.subtitle }}</div>
                <div v-if="s.status === 'loading'" class="cn-cmd-note">
                  {{ searchingLabel ?? t('loading') }}
                </div>
                <div v-else-if="s.status === 'failed'" class="cn-cmd-note">
                  {{ t('unavailable') }}
                </div>
                <button
                  v-for="entry in s.entries"
                  :id="optionId(entry)"
                  :key="entry.key"
                  type="button"
                  role="option"
                  tabindex="-1"
                  class="cn-cmd-item"
                  :class="{ active: indexOf(entry) === activeIdx }"
                  :aria-selected="indexOf(entry) === activeIdx"
                  @mousemove="activeIdx = indexOf(entry)"
                  @click="pick(entry)"
                >
                  <span v-if="entry.cmd.icon || s.icon" class="cn-cmd-icon">
                    <component :is="entry.cmd.icon ?? s.icon" class="nxt-icon-md" />
                  </span>
                  <span class="cn-cmd-text">
                    <span class="cn-cmd-label">
                      <template v-for="(part, pi) in labelParts(entry)" :key="pi">
                        <mark v-if="part.mark" class="cn-cmd-mark">{{ part.text }}</mark>
                        <template v-else>{{ part.text }}</template>
                      </template>
                    </span>
                    <span v-if="entry.cmd.hint" class="cn-cmd-hint-line">{{ entry.cmd.hint }}</span>
                    <span v-if="entry.cmd.snippet" class="cn-cmd-snippet">
                      <template v-for="(part, pi) in snippetParts(entry.cmd)" :key="pi">
                        <mark v-if="part.mark" class="cn-cmd-mark">{{ part.text }}</mark>
                        <template v-else>{{ part.text }}</template>
                      </template>
                    </span>
                  </span>
                  <span v-if="shortcutOf(entry.cmd)" class="cn-cmd-shortcut">
                    {{ shortcutOf(entry.cmd) }}
                  </span>
                  <ChevronRight
                    v-else-if="entry.cmd.children || entry.cmd.input"
                    class="cn-cmd-more nxt-icon-sm"
                  />
                </button>
              </div>
            </template>
            <div v-else-if="!searching" class="cn-cmd-empty">{{ emptyLabel ?? t('empty') }}</div>
            <div v-if="searching" class="cn-cmd-searching" role="status">
              {{ searchingLabel ?? t('searching') }}
            </div>
          </div>

          <aside v-if="preview" class="cn-cmd-preview" aria-live="polite">
            <div class="cn-cmd-preview-title">{{ preview.title }}</div>
            <div v-if="preview.subtitle" class="cn-cmd-preview-subtitle">
              {{ preview.subtitle }}
            </div>
            <dl v-if="preview.fields?.length" class="cn-cmd-preview-fields">
              <template v-for="f in preview.fields" :key="f.label">
                <dt>{{ f.label }}</dt>
                <dd>{{ f.value }}</dd>
              </template>
            </dl>
            <p v-if="preview.text" class="cn-cmd-preview-text">{{ preview.text }}</p>
          </aside>
        </div>

        <div class="cn-cmd-footer" aria-hidden="true">
          <span>
            <kbd>{{ t('key_up') }}</kbd>
            <kbd>{{ t('key_down') }}</kbd>
            {{ t('footer_move') }}
          </span>
          <span>
            <kbd>{{ t('key_enter') }}</kbd>
            {{ t('footer_open') }}
          </span>
          <span>
            <kbd>{{ formatBind('mod+enter') }}</kbd>
            {{ t('footer_new_tab') }}
          </span>
          <span>
            <kbd>{{ t('key_tab') }}</kbd>
            {{ t('footer_actions') }}
          </span>
          <span>
            <kbd>{{ t('key_esc') }}</kbd>
            {{ t('footer_close') }}
          </span>
        </div>
        <div class="cn-cmd-sr" role="status" aria-live="polite">
          {{ liveStatus }}
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.cn-cmd-overlay {
  position: fixed;
  inset: 0;
  background: var(--nxt-overlay-bg);
  backdrop-filter: blur(calc(var(--nxt-size-unit) * 0.125));
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 14vh;
  z-index: var(--nxt-z-modal);
}

.cn-cmd-panel {
  position: relative;
  width: min(var(--nxt-panel-width-4xl), calc(100vw - var(--nxt-space-xl)));
  max-height: 70vh;
  background: var(--nxt-surface-raised-bg);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-lg);
  box-shadow: var(--nxt-shadow-lg);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.cn-cmd-panel.has-preview {
  width: min(
    calc(var(--nxt-panel-width-4xl) + var(--nxt-panel-width-sm)),
    calc(100vw - var(--nxt-space-xl))
  );
}

.cn-cmd-search {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-sm);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
}
.cn-cmd-search-icon {
  color: var(--nxt-fg-subtle);
  flex-shrink: 0;
}
.cn-cmd-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  flex-shrink: 0;
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-brand-subtle-fg);
  background: var(--nxt-brand-subtle-bg);
  border-radius: var(--nxt-radius-sm);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
}
.cn-cmd-crumb {
  color: var(--nxt-fg-muted);
  background: var(--nxt-surface-sunken-bg);
}
.cn-cmd-input {
  flex: 1;
  min-width: 0;
  background: transparent;
  border: none;
  outline: none;
  font-size: var(--nxt-font-size-md);
  color: var(--nxt-fg);
  font-family: inherit;
}
.cn-cmd-input::placeholder {
  color: var(--nxt-fg-subtle);
}
.cn-cmd-suggestion {
  padding: var(--nxt-space-2xs) var(--nxt-space-sm);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
}

.cn-cmd-hint {
  font-family: var(--nxt-font-mono);
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg-subtle);
  background: var(--nxt-brand-subtle-bg);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-sm);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  flex-shrink: 0;
}

.cn-cmd-body {
  display: flex;
  flex: 1;
  min-height: 0;
}

.cn-cmd-list {
  flex: 1;
  overflow-y: auto;
  padding: var(--nxt-space-2xs);
  min-height: 0;
  min-width: 0;
}

.cn-cmd-group {
  margin-bottom: var(--nxt-space-2xs);
}
.cn-cmd-group-label {
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--nxt-fg-subtle);
  padding: var(--nxt-space-xs) var(--nxt-space-xs) var(--nxt-space-2xs);
}
.cn-cmd-subgroup-label {
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
}
.cn-cmd-note {
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-subtle);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs) var(--nxt-space-xs);
}

.cn-cmd-item {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  width: 100%;
  padding: var(--nxt-space-xs);
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--nxt-fg);
  text-align: left;
  font-family: inherit;
  font-size: var(--nxt-font-size-sm);
}
.cn-cmd-item.active {
  background: var(--nxt-brand-subtle-bg);
}
.cn-cmd-icon {
  display: grid;
  place-items: center;
  color: var(--nxt-fg);
  flex-shrink: 0;
}
.cn-cmd-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.cn-cmd-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cn-cmd-mark {
  background: transparent;
  color: var(--nxt-brand-subtle-fg);
  text-decoration: underline;
  text-underline-offset: 0.15em;
}
.cn-cmd-hint-line,
.cn-cmd-snippet {
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-subtle);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cn-cmd-shortcut {
  font-family: var(--nxt-font-mono);
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-subtle);
  background: var(--nxt-brand-subtle-bg);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-sm);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  flex-shrink: 0;
}
.cn-cmd-item.active .cn-cmd-shortcut {
  color: var(--nxt-fg);
  background: var(--nxt-brand-subtle-bg);
  border: var(--nxt-hairline) solid var(--nxt-border-strong);
}
.cn-cmd-more {
  color: var(--nxt-fg-subtle);
  flex-shrink: 0;
}

.cn-cmd-preview {
  width: var(--nxt-panel-width-sm);
  flex-shrink: 0;
  overflow-y: auto;
  border-left: var(--nxt-hairline) solid var(--nxt-border);
  padding: var(--nxt-space-sm);
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg);
}
.cn-cmd-preview-title {
  font-size: var(--nxt-font-size-md);
  overflow-wrap: anywhere;
}
.cn-cmd-preview-subtitle {
  color: var(--nxt-fg-muted);
  margin-top: var(--nxt-space-2xs);
}
.cn-cmd-preview-fields {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--nxt-space-2xs) var(--nxt-space-sm);
  margin: var(--nxt-space-sm) 0 0;
}
.cn-cmd-preview-fields dt {
  color: var(--nxt-fg-subtle);
}
.cn-cmd-preview-fields dd {
  margin: 0;
  overflow-wrap: anywhere;
}
.cn-cmd-preview-text {
  margin: var(--nxt-space-sm) 0 0;
  color: var(--nxt-fg-muted);
  white-space: pre-line;
  overflow-wrap: anywhere;
}

.cn-cmd-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-sm);
  padding: var(--nxt-space-2xs) var(--nxt-space-sm);
  border-top: var(--nxt-hairline) solid var(--nxt-border);
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-subtle);
}
.cn-cmd-footer kbd {
  font-family: var(--nxt-font-mono);
  font-size: var(--nxt-font-size-xs);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-sm);
  padding: 0 var(--nxt-space-2xs);
  margin-right: var(--nxt-space-2xs);
}

.cn-cmd-searching {
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-xs);
}

.cn-cmd-empty {
  padding: var(--nxt-space-lg);
  text-align: center;
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-sm);
}

.cn-cmd-sr {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

@media (max-width: 760px) {
  .cn-cmd-preview,
  .cn-cmd-footer {
    display: none;
  }
}
</style>
